import { useMemo, useState } from 'react';
import {
  Stack, Title, Group, Button, TextInput, Table, Badge, Text, ActionIcon,
  Tooltip, Skeleton, Alert, Progress,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconPlus, IconSearch, IconTrash } from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { DateTime } from 'luxon';
import fuzzysort from 'fuzzysort';
import { SortableTh } from '@/shared/components/SortableTh';
import { useTableSort, type TableSortColumn } from '@/shared/hooks/useTableSort';
import { useSubAccounts, type SubAccountListItem } from '../../hooks/useSubAccounts';
import { useDeleteSubAccount } from '../../hooks/useSubAccountMutations';
import { AccountUtils } from '../../AccountUtils';
import { UsageExportButton } from './UsageExportButton';

type SubAccountSortKey = 'name' | 'status' | 'apps' | 'storage' | 'transfer' | 'created';

const SUBACCOUNT_SORT: Record<SubAccountSortKey, TableSortColumn<SubAccountListItem>> = {
  name: { accessor: (i) => i.account.cfg.label },
  status: { accessor: (i) => i.account.cfg.customerStateType },
  apps: { accessor: (i) => i.appCfgsInUse, defaultDir: 'desc' },
  storage: { accessor: (i) => i.storageBytesUsed, defaultDir: 'desc' },
  transfer: { accessor: (i) => i.transferBytesUsed, defaultDir: 'desc' },
  created: { accessor: (i) => i.account.cfg.created, defaultDir: 'desc' },
};

export function SubAccountsPage() {
  const navigate = useNavigate();
  const { subAccounts, subaccountLimit, isLoading } = useSubAccounts();
  const deleteMutation = useDeleteSubAccount();
  const [search, setSearch] = useState('');

  const atLimit = subaccountLimit !== null && subAccounts.length >= subaccountLimit;

  // Search filters; the sort hook owns final ordering (seeded name-asc to match
  // the prior default). Fuzzy relevance yields to the active column sort.
  const searchedAccounts = useMemo(() => {
    if (!search.trim()) return subAccounts;
    return fuzzysort
      .go(search, subAccounts, { keys: ['account.cfg.label'], threshold: -1000 })
      .map(r => r.obj);
  }, [subAccounts, search]);

  const { sorted, sortKey, sortDir, onSort } = useTableSort(
    searchedAccounts,
    SUBACCOUNT_SORT,
    { key: 'name', dir: 'asc' },
  );

  function handleDelete(item: SubAccountListItem) {
    const label = item.account.cfg.label;
    modals.openConfirmModal({
      title: 'Delete Sub-Account',
      children: (
        <Text size="sm">
          Are you sure you want to delete <Text span fw={600}>{label}</Text>?
          This action cannot be undone.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await deleteMutation.mutateAsync(item.account.cfg.cId);
          notifications.show({ title: 'Deleted', message: `Sub-account "${label}" removed.`, color: 'green' });
        } catch (err) {
          notifications.show({
            title: 'Error',
            message: err instanceof Error ? err.message : 'Failed to delete sub-account.',
            color: 'red',
          });
        }
      },
    });
  }

  if (isLoading) {
    return (
      <Stack gap="lg">
        <Title order={2}>Sub-Accounts</Title>
        <Skeleton height={40} radius="md" />
        <Skeleton height={400} radius="md" />
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Title order={2}>Sub-Accounts</Title>
        <Group gap="sm">
          <UsageExportButton />
          <Tooltip
            label={atLimit ? `Sub-account limit reached (${subAccounts.length}/${subaccountLimit})` : ''}
            disabled={!atLimit}
          >
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => navigate({ to: '/account/sub-accounts/new' })}
              disabled={atLimit}
            >
              Add Sub-Account
            </Button>
          </Tooltip>
        </Group>
      </Group>

      {atLimit && (
        <Alert color="yellow" variant="light">
          Sub-account limit reached ({subAccounts.length}/{subaccountLimit}).
          Contact support to increase your limit.
        </Alert>
      )}

      <TextInput
        placeholder="Search by name..."
        leftSection={<IconSearch size={16} />}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
        aria-label="Search sub-accounts"
      />

      {sorted.length === 0 ? (
        <Text c="dimmed" ta="center" py="xl">
          {search ? 'No sub-accounts match your search.' : 'No sub-accounts yet.'}
        </Text>
      ) : (
        <Table highlightOnHover aria-label="Sub-accounts">
          <Table.Thead>
            <Table.Tr>
              <SortableTh columnKey="name" sortKey={sortKey} sortDir={sortDir} onSort={onSort}>Name</SortableTh>
              <SortableTh columnKey="status" sortKey={sortKey} sortDir={sortDir} onSort={onSort}>Status</SortableTh>
              <SortableTh columnKey="apps" sortKey={sortKey} sortDir={sortDir} onSort={onSort}>Apps</SortableTh>
              <SortableTh columnKey="storage" sortKey={sortKey} sortDir={sortDir} onSort={onSort}>Storage</SortableTh>
              <SortableTh columnKey="transfer" sortKey={sortKey} sortDir={sortDir} onSort={onSort}>Transfer</SortableTh>
              <SortableTh columnKey="created" sortKey={sortKey} sortDir={sortDir} onSort={onSort}>Created</SortableTh>
              <Table.Th ta="right">Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sorted.map(item => (
              <SubAccountRow
                key={item.account.cfg.cId}
                item={item}
                onNavigate={() => navigate({ to: '/account/sub-accounts/$id', params: { id: String(item.account.cfg.cId) } })}
                onDelete={() => handleDelete(item)}
              />
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  );
}

// ── Row component ──────────────────────────────────────────────────

function SubAccountRow({
  item,
  onNavigate,
  onDelete,
}: {
  item: SubAccountListItem;
  onNavigate: () => void;
  onDelete: () => void;
}) {
  const cfg = item.account.cfg;
  const limits = cfg.quota.limits;
  const appLimit = cfg.quota.features.app.appCfgLimit;

  return (
    <Table.Tr>
      <Table.Td>
        <Text
          size="sm"
          fw={500}
          c="blue"
          style={{ cursor: 'pointer' }}
          onClick={onNavigate}
        >
          {cfg.label}
        </Text>
      </Table.Td>
      <Table.Td>
        <Badge
          color={cfg.customerStateType === 'active' ? 'green' : 'gray'}
          variant="light"
          size="sm"
        >
          {cfg.customerStateType === 'active' ? 'Active' : 'Inactive'}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Text size="xs">
          {item.appCfgsInUse} / {appLimit ?? '∞'}
        </Text>
      </Table.Td>
      <Table.Td>
        <UsageCell used={item.storageBytesUsed} limit={limits.storageBytesLimit} />
      </Table.Td>
      <Table.Td>
        <UsageCell used={item.transferBytesUsed} limit={limits.transferBytesLimit} />
      </Table.Td>
      <Table.Td>
        <Text size="sm">
          {DateTime.fromSeconds(cfg.created).toLocaleString(DateTime.DATE_MED)}
        </Text>
      </Table.Td>
      <Table.Td>
        <Group gap="xs" justify="flex-end">
          <Tooltip label="Delete">
            <ActionIcon variant="subtle" color="red" onClick={onDelete} aria-label={`Delete ${cfg.label}`}>
              <IconTrash size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Table.Td>
    </Table.Tr>
  );
}

// ── Usage cell with progress bar ──────────────────────────────────

function UsageCell({ used, limit }: { used: number; limit: number | null }) {
  const percent = AccountUtils.usagePercent(used, limit);
  const usedGB = AccountUtils.formatBytesAsGB(used);

  if (percent === null) {
    return (
      <Stack gap={2}>
        <Text size="xs">{usedGB}</Text>
        <Text size="xs" c="dimmed">Unlimited</Text>
      </Stack>
    );
  }

  const color = AccountUtils.usageColor(percent);
  const limitGB = AccountUtils.formatBytesAsGB(limit!);

  return (
    <Stack gap={2}>
      <Text size="xs">{usedGB} / {limitGB}</Text>
      <Progress value={percent} color={color} size="sm" aria-label={`Usage: ${Math.round(percent)}%`} />
    </Stack>
  );
}
